import { toast } from "react-toastify";
import { VideoSource } from "../../Enums/enums";
import { rawIdsOf, sameVideoChoice } from "./batchItems";

/**
 * Edited / raw clips for the open WGID. A saved edit has its video built in, so
 * a tab whose video changes starts again from the template: its saved edit is
 * dropped (one clear warning first), and the card's rawVideoIds is what every
 * other view reads.
 *
 * Returns `handleVideoChoice({ ids, scope: "all" | "one" })`.
 */
export default function useVideoChoice({ activeItem, activeVideo, hasUnsaved, onConfigChange, setMark, clearSaveNote, askConfirm }) {
  return ({ ids, scope }) => {
    const item = activeItem;
    const next = { rawVideoIds: ids };
    // Duplicates reuse another platform's video, so they have none to switch.
    const targets = (scope === "all" && activeVideo ? activeVideo.items : [item]).filter((t) => t.config?.source !== VideoSource.DUPLICATE);
    const changed = targets.filter((t) => !sameVideoChoice(t.config, next));
    const what = ids.length > 0 ? `${ids.length} raw clip${ids.length !== 1 ? "s" : ""}` : "the edited video";
    if (changed.length === 0) {
      toast.info(`${item.wgid} already uses ${what} there.`);
      return;
    }
    const apply = () => {
      changed.forEach((t) => onConfigChange(t.brandName, t.wgid, t.platform, { rawVideoIds: ids, renderPayload: null, batchApplied: null }));
      setMark(item.key, null);
      clearSaveNote();
      toast.success(`${item.wgid} now uses ${what} on ${changed.map((t) => t.platform).join(", ")}.`);
    };
    const unsavedHere = hasUnsaved && changed.some((t) => t.key === item.key);
    const savedLost = changed.filter((t) => t.config?.renderPayload);
    if (!unsavedHere && savedLost.length === 0) {
      apply();
      return;
    }
    const from = rawIdsOf(item.config).length > 0 ? "raw clips" : "the edited video";
    const parts = [];
    if (unsavedHere) parts.push(`${item.platform} has unsaved edits that will be lost.`);
    if (savedLost.length > 0) {
      parts.push(
        `Saved edits on ${savedLost.map((t) => t.platform).join(" and ")} were made on ${from}; ${savedLost.length === 1 ? "that tab" : "those tabs"} will restart from the template.`,
      );
    }
    askConfirm(`Switch ${item.wgid} to ${ids.length > 0 ? "raw clips" : "the edited video"}?`, parts.join(" "), "Switch video", apply);
  };
}
